import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, IconButton, InputAdornment, MenuItem, TableCell, TableRow, TextField, Theme, Tooltip, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';

import {
  createApiCredentials,
  deleteApiCredential,
  getUser,
  getUserRoles,
  getApiCredentials,
  regenerateApiCredentials,
  setUserPin,
  updateApiCredential,
  updateUser,
} from '../../api/usersApi/usersApi';
import FormTextField from '../../components/formFields/FormTextField';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormCopyTextField from '../../components/formFields/FormCopyTextField';
import { MANAGE_ACCOUNT_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { manageAccountFormSchema } from '../../helpers/formSchemas/formSchemas';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import CommonButton from '../../components/buttons/CommonButton';
import { ActiveSortField, ErrorData, SelectOption } from '../../types';
import VerificationPopup from '../../components/VerificationPopup';
import Popup from '../../components/Popup';
import TableContainer from '../../components/Table/TableContainer';
import NoDataText from '../../components/NoDataText';

import SetPinCodePopup from './SetPinCodePopup';
import Loader from '../../components/Loader';

const useStyles = makeStyles()((theme: Theme) => ({
  formWrapper: {
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '896px',
    padding: '32px',
    gap: '32px',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    backgroundColor: theme.palette.primary.light,
    boxSizing: 'border-box',

    '@media (max-width: 725px)': {
      padding: '12px',
    },
  },
  formContentContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '32px',

    '@media (max-width: 725px)': {
      flexDirection: 'column',
    },
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 0,
    gap: '32px',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldHeadline: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
  },
  text: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '14.4px',
    color: theme.palette.text.secondary,
  },
  apiCredentialsContainer: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    boxSizing: 'border-box',
  },
  apiCredentialsFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  copyTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.info.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.info.main,
      },
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.common.white,
      color: theme.palette.common.white,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.disabled,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.common.white,
    },
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    maxWidth: '416px',
    gap: '16px',

    '@media (max-width: 768px)': {
      alignSelf: 'flex-start',
    },
  },
  apiCredentialsTableActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  apiCredentialsActionButton: {
    color: theme.palette.common.white,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    padding: '6px',

    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  generatedSecretContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    width: '100%',
    boxSizing: 'border-box',
  },
  loadingOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1400,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  roleSelectMenuPaper: {
    backgroundColor: theme.palette.secondary.dark,
  },
  roleSelectMenuItem: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    color: theme.palette.common.white,
  },
}));

export interface ManageAccountFormSubmitProps {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  isPinSet: boolean;
}

interface UserApiCredentialsState {
  name: string | null;
  clientSecret: string | null;
  clientId: string | null;
  roles: string[];
}

interface GeneratedApiSecretState {
  name: string;
  clientId: string;
  clientSecret: string;
}

const API_CREDENTIALS_TABLE_LABELS = [
  {
    accessor: '',
    isSortable: false,
    label: 'Name',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Client ID',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Actions',
  },
];

interface ManageAccountViewProps {
  tab: 'home' | 'api-service';
}

const ManageAccountView = ({ tab }: ManageAccountViewProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();

  const canCreateApiService = useHasPermission('API Service', 'Create');
  const canEditApiService = useHasPermission('API Service', 'Edit');
  const canDeleteApiService = useHasPermission('API Service', 'Delete');

  const userId = localStorage.getItem('userId');

  const [isSetNewPinPopupOpen, setIsSetNewPinPopupOpen] = useState<boolean>(false);
  const [isVerificationPopupOpen, setIsVerificationPopupOpen] = useState<boolean>(false);
  const [currentPin, setCurrentPin] = useState<string | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isApiCredentialsLoading, setIsApiCredentialsLoading] = useState<boolean>(false);
  const [isRolesLoading, setIsRolesLoading] = useState<boolean>(false);
  const [isPinActionLoading, setIsPinActionLoading] = useState<boolean>(false);
  const [apiCredentialsList, setApiCredentialsList] = useState<UserApiCredentialsState[]>([]);
  const [generatedApiSecret, setGeneratedApiSecret] = useState<GeneratedApiSecretState | null>(null);
  const [isCreateApiCredentialsPopupOpen, setIsCreateApiCredentialsPopupOpen] =
    useState<boolean>(false);
  const [isRegenerateApiCredentialsPopupOpen, setIsRegenerateApiCredentialsPopupOpen] =
    useState<boolean>(false);
  const [isDeleteApiCredentialsPopupOpen, setIsDeleteApiCredentialsPopupOpen] =
    useState<boolean>(false);
  const [isEditApiCredentialsPopupOpen, setIsEditApiCredentialsPopupOpen] = useState<boolean>(false);
  const [apiRoleOptions, setApiRoleOptions] = useState<SelectOption[]>([]);
  const [selectedApiRoles, setSelectedApiRoles] = useState<string[]>([]);
  const [newApiCredentialName, setNewApiCredentialName] = useState<string>('');
  const [editedApiCredentialName, setEditedApiCredentialName] = useState<string>('');
  const [editedApiRoles, setEditedApiRoles] = useState<string[]>([]);
  const [selectedApiCredential, setSelectedApiCredential] = useState<UserApiCredentialsState | null>(null);
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(manageAccountFormSchema),
    defaultValues: MANAGE_ACCOUNT_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid, isDirty },
  } = formMethods;

  const userIdValue = watch('id');
  const isPinSet = watch('isPinSet');

  const clearGeneratedApiSecret = () => {
    setGeneratedApiSecret(null);
  };

  const onSubmitForm = (values: ManageAccountFormSubmitProps) => {
    clearGeneratedApiSecret();
    setIsFormSubmitting(true);

    const submitData = {
      firstName: values.firstName,
      lastName: values.lastName,
    };

    updateUser(userIdValue, submitData)
      .then((res) => {
        if (res.data) {
          setAlert((prev) => ({
            ...prev,
            message: 'Account updated successfully',
            type: 'success',
          }));

          reset({
            id: res.data.id,
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            email: res.data.email,
            phone: res.data.phone,
            role:
              Array.isArray(res.data.roles) && res.data.roles.length > 0 && typeof res.data.roles[0] !== 'string'
                ? (res.data.roles[0] as { roleName: string }).roleName
                : res.data.role,
            isPinSet: res.data.isPinSet,
          });
        }

        setIsFormSubmitting(false);
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => {
    clearGeneratedApiSecret();
    reset();
  };

  const handleCloseSetNewPinPopup = () => setIsSetNewPinPopupOpen(false);
  const handleCloseVerificationPopup = () => setIsVerificationPopupOpen(false);
  const handleOpenSetNewPinPopup = () => setIsSetNewPinPopupOpen(true);
  const handleOpenVerificationPopup = () => setIsVerificationPopupOpen(true);

  const getUserData = () => {
    setIsLoading(true);

    if (userId) {
      getUser(userId)
        .then(({ data }) => {
          reset({
            id: data.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            role:
              Array.isArray(data.roles) && data.roles.length > 0 && typeof data.roles[0] !== 'string'
                ? (data.roles[0] as { roleName: string }).roleName
                : data.role,
            isPinSet: data.isPinSet,
          });
        })
        .catch(() => {
          setAlert((prev) => ({
            ...prev,
            message: 'Cannot load account data.',
            type: 'error',
          }));
        })
        .finally(() => setIsLoading(false));
    }
  };

  const setPinCode = (newPin: string) => {
    if (!userId) return;

    clearGeneratedApiSecret();

    const oldPin = currentPin || undefined;
    setIsPinActionLoading(true);

    setUserPin(Number(userId), newPin, oldPin)
      .then(() => {
        getUserData();
        handleCloseSetNewPinPopup();
        setAlert((prev) => ({
          ...prev,
          message: 'PIN has been set successfully.',
          type: 'success',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => setIsPinActionLoading(false));
  };

  const normalizeCredentialItem = (credential: Record<string, unknown>): UserApiCredentialsState | null => {
    const clientId = String(credential.client_id ?? credential.clientId ?? '');
    const rawRoles = credential.roles;

    const normalizedRoles = Array.isArray(rawRoles)
      ? rawRoles
          .map((role) => {
            if (typeof role === 'number' || typeof role === 'string') {
              return String(role);
            }

            if (role && typeof role === 'object') {
              const rolePayload = role as { id?: number | string; roleId?: number | string };
              const roleIdentifier = rolePayload.id ?? rolePayload.roleId;

              return roleIdentifier !== undefined && roleIdentifier !== null ? String(roleIdentifier) : null;
            }

            return null;
          })
          .filter((role): role is string => Boolean(role))
      : [];

    if (!clientId || clientId === 'null' || clientId === 'undefined') {
      return null;
    }

    return {
      name: (credential.name as string | null) || '-',
      clientId,
      roles: normalizedRoles,
      clientSecret:
        credential.client_secret && typeof credential.client_secret === 'string'
          ? credential.client_secret
          : null,
    };
  };

  const normalizeApiCredentialsResponse = (data: unknown): UserApiCredentialsState[] => {
    if (Array.isArray(data)) {
      return data
        .map((item) => normalizeCredentialItem(item as Record<string, unknown>))
        .filter((item): item is UserApiCredentialsState => Boolean(item));
    }

    if (data && typeof data === 'object') {
      const payload = data as Record<string, unknown>;

      if (Array.isArray(payload.credentials)) {
        return payload.credentials
          .map((item) => normalizeCredentialItem(item as Record<string, unknown>))
          .filter((item): item is UserApiCredentialsState => Boolean(item));
      }

      const singleCredential = normalizeCredentialItem(payload);

      return singleCredential ? [singleCredential] : [];
    }

    return [];
  };

  const loadApiCredentials = () => {
    if (!userId) return;

    setIsApiCredentialsLoading(true);

    getApiCredentials(Number(userId))
      .then(({ data }) => {
        setApiCredentialsList(normalizeApiCredentialsResponse(data));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Cannot load API credentials.',
          type: 'error',
        });
      })
      .finally(() => setIsApiCredentialsLoading(false));
  };

  const handleCloseCreateApiCredentialsPopup = () => {
    setIsCreateApiCredentialsPopupOpen(false);
    setNewApiCredentialName('');
    setSelectedApiRoles([]);
  };

  const handleCloseRegenerateApiCredentialsPopup = () => {
    setIsRegenerateApiCredentialsPopupOpen(false);
    setSelectedApiCredential(null);
  };

  const handleCloseDeleteApiCredentialsPopup = () => {
    setIsDeleteApiCredentialsPopupOpen(false);
    setSelectedApiCredential(null);
  };

  const handleCloseEditApiCredentialsPopup = () => {
    setIsEditApiCredentialsPopupOpen(false);
    setSelectedApiCredential(null);
    setEditedApiCredentialName('');
    setEditedApiRoles([]);
  };

  const handleConfirmCreateApiCredentials = () => {
    if (!userId) return;

    const selectedRoleId = selectedApiRoles[0];

    if (!newApiCredentialName.trim()) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter API credential name.',
        type: 'error',
      }));

      return;
    }

    if (!selectedRoleId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please select at least one role before creating API credentials.',
        type: 'error',
      }));

      return;
    }

    setIsApiCredentialsLoading(true);

    createApiCredentials(Number(userId), [selectedRoleId], newApiCredentialName.trim())
      .then(({ data }) => {
        setGeneratedApiSecret({
          name: data.name || '-',
          clientId: data.client_id || '-',
          clientSecret: data.client_secret || '-',
        });
        handleCloseCreateApiCredentialsPopup();
        loadApiCredentials();
        setAlert((prev) => ({
          ...prev,
          message: 'API credentials created successfully.',
          type: 'success',
        }));
        setAlert((prev) => ({
          ...prev,
          message:
            'Client secret is shown only once. Copy and store it in a secure place. If lost, regenerate API credentials.',
          type: 'warning',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => setIsApiCredentialsLoading(false));
  };

  const getUserRoleOptions = () => {
    setIsRolesLoading(true);

    getUserRoles()
      .then(({ data }) => {
        const roleOptions = data.roles.map((role) => ({
          id: String(role.id),
          label: role.roleName,
        }));

        setApiRoleOptions(roleOptions);
        setSelectedApiRoles(roleOptions[0]?.id ? [roleOptions[0].id] : []);
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Cannot load roles.',
          type: 'error',
        });
      })
      .finally(() => setIsRolesLoading(false));
  };

  const handleOpenRegenerateApiCredentialsPopup = (credential: UserApiCredentialsState) => {
    clearGeneratedApiSecret();
    setSelectedApiCredential(credential);
    setIsRegenerateApiCredentialsPopupOpen(true);
  };

  const handleOpenDeleteApiCredentialsPopup = (credential: UserApiCredentialsState) => {
    clearGeneratedApiSecret();
    setSelectedApiCredential(credential);
    setIsDeleteApiCredentialsPopupOpen(true);
  };

  const handleOpenEditApiCredentialsPopup = (credential: UserApiCredentialsState) => {
    clearGeneratedApiSecret();
    setSelectedApiCredential(credential);
    setEditedApiCredentialName(credential.name || '');
    setEditedApiRoles(credential.roles?.length ? [credential.roles[0]] : []);
    setIsEditApiCredentialsPopupOpen(true);
  };

  const handleConfirmRegenerateApiCredentials = () => {
    if (!userId) return;
    if (!selectedApiCredential?.clientId) return;

    setIsApiCredentialsLoading(true);

    regenerateApiCredentials(Number(userId), selectedApiCredential.clientId)
      .then(({ data }) => {
        setGeneratedApiSecret({
          name: data.name || '-',
          clientId: data.client_id || '-',
          clientSecret: data.client_secret || '-',
        });
        handleCloseRegenerateApiCredentialsPopup();
        loadApiCredentials();
        setAlert((prev) => ({
          ...prev,
          message: 'API credentials regenerated successfully.',
          type: 'success',
        }));
        setAlert((prev) => ({
          ...prev,
          message:
            'New client secret is shown only once. Copy and store it in a secure place. If lost, regenerate API credentials again.',
          type: 'warning',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => setIsApiCredentialsLoading(false));
  };

  const handleConfirmDeleteApiCredentials = () => {
    if (!userId) return;
    if (!selectedApiCredential?.clientId) return;

    clearGeneratedApiSecret();

    setIsApiCredentialsLoading(true);

    deleteApiCredential(Number(userId), selectedApiCredential.clientId)
      .then(() => {
        handleCloseDeleteApiCredentialsPopup();
        loadApiCredentials();
        setAlert((prev) => ({
          ...prev,
          message: 'API credential deleted successfully.',
          type: 'success',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => setIsApiCredentialsLoading(false));
  };

  const handleConfirmEditApiCredentials = () => {
    if (!userId) return;
    if (!selectedApiCredential?.clientId) return;

    const selectedRoleId = editedApiRoles[0];

    clearGeneratedApiSecret();

    if (!editedApiCredentialName.trim()) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter API credential name.',
        type: 'error',
      }));

      return;
    }

    if (!selectedRoleId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please select at least one role before updating API credentials.',
        type: 'error',
      }));

      return;
    }

    setIsApiCredentialsLoading(true);

    updateApiCredential(Number(userId), selectedApiCredential.clientId, editedApiCredentialName.trim(), [selectedRoleId])
      .then(() => {
        handleCloseEditApiCredentialsPopup();
        loadApiCredentials();
        setAlert((prev) => ({
          ...prev,
          message: 'API credential updated successfully.',
          type: 'success',
        }));
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => setIsApiCredentialsLoading(false));
  };

  const handleCopyApiCredential = (value: string, fieldLabel: string) => {
    navigator.clipboard.writeText(value);
    setAlert((prev) => ({
      ...prev,
      message: `${fieldLabel} copied successfully.`,
      type: 'success',
    }));
  };

  useEffect(() => {
    getUserData();
    getUserRoleOptions();
  }, [userId]);

  useEffect(() => {
    if (tab === 'api-service') {
      loadApiCredentials();

      return;
    }

    clearGeneratedApiSecret();
  }, [tab]);

  useEffect(() => () => clearGeneratedApiSecret(), []);

  if (isLoading) {
    return <Loader colorType="warning" />;
  }

  const isRequestLoading = isFormSubmitting || isApiCredentialsLoading || isRolesLoading || isPinActionLoading;

  return (
    <Box className={classes.formWrapper}>
      {isRequestLoading && (
        <Box className={classes.loadingOverlay}>
          <Loader colorType="warning" size={60} />
        </Box>
      )}

      <FormProvider {...formMethods}>
        <form className={classes.formContainer}>
          {tab === 'home' && (
            <>
              <Box className={classes.formContentContainer}>
                <Box className={classes.formColumn}>
                  <Box className={classes.formSection}>
                    <Typography className={classes.fieldHeadline}>Identity</Typography>
                    <FormCopyTextField entityName="Account" name="id" label="User ID" isReadOnly />
                    <FormTextField name="role" label="Role *" disabled />
                    <FormTextField name="firstName" label="First Name *" />
                    <FormTextField name="lastName" label="Last Name *" />
                  </Box>

                  <Box className={classes.formSection}>
                    <Typography className={classes.fieldHeadline}>Credentials</Typography>
                    <FormTextField name="email" label="Email Address *" disabled />
                    <FormTextField name="phone" label="Mobile Number *" disabled />
                    <Box sx={{ display: 'none' }}>
                      <CommonButton buttonText="Change Password" type="infoDark" maxWidth="185px" />
                    </Box>
                  </Box>
                </Box>

                <Box className={classes.formColumn}>
                  <Box className={classes.formSection}>
                    <Typography className={classes.fieldHeadline}>Actions</Typography>
                    <Typography className={classes.text}>
                      {isPinSet ? (
                        <>
                          {'If you forgot your PIN, please contact Client'}
                          <br />
                          {'Support to reset it'}
                        </>
                      ) : (
                        'Set up the PIN for settlement'
                      )}
                    </Typography>
                    <CommonButton
                      buttonText={isPinSet ? 'Change PIN' : 'Set PIN'}
                      type="infoDark"
                      maxWidth="163px"
                      onButtonClick={isPinSet ? handleOpenVerificationPopup : handleOpenSetNewPinPopup}
                    />
                  </Box>
                </Box>
              </Box>

              <Box className={classes.actionButtons}>
                <FormSubmitButton
                  disabled={!isValid || !isDirty || isFormSubmitting}
                  buttonIcon={<SaveIcon />}
                  buttonText="Save"
                  onClick={handleSubmit(onSubmitForm)}
                />
                <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
              </Box>
            </>
          )}

          {tab === 'api-service' && (
            <Box className={classes.formSection}>
              <Typography className={classes.fieldHeadline}>API Service</Typography>
              <Typography className={classes.text}>Manage API credentials</Typography>

              {canCreateApiService && (
                <CommonButton
                  buttonText="Create API Credential"
                  type="infoDark"
                  maxWidth="260px"
                  disabled={isApiCredentialsLoading}
                  onButtonClick={() => setIsCreateApiCredentialsPopupOpen(true)}
                />
              )}

              {generatedApiSecret && (
                <Box className={classes.generatedSecretContainer}>
                  <Typography className={classes.text}>New client secret (shown only once)</Typography>
                  <TextField
                    className={classes.copyTextField}
                    label="Credential Name"
                    value={generatedApiSecret.name}
                    fullWidth
                    disabled
                  />
                  <TextField
                    className={classes.copyTextField}
                    label="Client ID"
                    value={generatedApiSecret.clientId}
                    fullWidth
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            disableRipple
                            sx={{ color: 'white' }}
                            onClick={() => handleCopyApiCredential(generatedApiSecret.clientId, 'Client ID')}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    className={classes.copyTextField}
                    label="Client Secret"
                    value={generatedApiSecret.clientSecret}
                    fullWidth
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            disableRipple
                            sx={{ color: 'white' }}
                            onClick={() => handleCopyApiCredential(generatedApiSecret.clientSecret, 'Client secret')}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {!isApiCredentialsLoading && apiCredentialsList.length === 0 ? (
                <NoDataText />
              ) : (
                <Box sx={{ '& > div': { overflow: 'hidden !important' }, '& table': { minWidth: '0 !important', width: '100%' } }}>
                <TableContainer
                  headerLabels={API_CREDENTIALS_TABLE_LABELS}
                  activeSortField={activeSortField}
                  setActiveSortField={setActiveSortField}
                >
                  {apiCredentialsList.map((credential) => (
                    <TableRow key={credential.clientId}>
                      <TableCell style={{ width: '30%' }}>{credential.name || '-'}</TableCell>
                      <TableCell style={{ width: '50%', wordBreak: 'break-all', whiteSpace: 'normal', maxWidth: 'none', overflow: 'visible' }}>{credential.clientId}</TableCell>
                      <TableCell style={{ width: '20%' }}>
                        <Box className={classes.apiCredentialsTableActions}>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              className={classes.apiCredentialsActionButton}
                              onClick={() => handleOpenEditApiCredentialsPopup(credential)}
                              disableRipple
                              disabled={!canEditApiService}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Regenerate" arrow>
                            <IconButton
                              className={classes.apiCredentialsActionButton}
                              onClick={() => handleOpenRegenerateApiCredentialsPopup(credential)}
                              disableRipple
                              disabled={!canEditApiService}
                            >
                              <AutorenewOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              className={classes.apiCredentialsActionButton}
                              onClick={() => handleOpenDeleteApiCredentialsPopup(credential)}
                              disableRipple
                              disabled={!canDeleteApiService}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </form>
      </FormProvider>

      <VerificationPopup
        isOpen={isVerificationPopupOpen}
        onSuccess={(pin) => {
          handleCloseVerificationPopup();
          handleOpenSetNewPinPopup();
          setCurrentPin(pin);
        }}
        onCancel={handleCloseVerificationPopup}
      />
      <SetPinCodePopup
        isOpen={isSetNewPinPopupOpen}
        onMainButtonClick={setPinCode}
        onSecondaryButtonClick={handleCloseSetNewPinPopup}
      />
      <Popup
        isOpen={isCreateApiCredentialsPopupOpen}
        headlineText="Create API Credentials"
        contentText="API credentials are not available for this account. Do you want to create them now?"
        PopupContent={
          <Box sx={{ width: '100%', maxWidth: '416px', display: 'flex', flexDirection: 'column' }}>
            <Box mt="8px">
              <TextField
                className={classes.copyTextField}
                label="Credential Name *"
                value={newApiCredentialName}
                fullWidth
                onChange={(event) => setNewApiCredentialName(event.target.value)}
              />
            </Box>
            <Box mt="12px">
              <TextField
                className={classes.copyTextField}
                label="Select Roles For Generated Token *"
                value={selectedApiRoles[0] || ''}
                fullWidth
                select
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      className: classes.roleSelectMenuPaper,
                    },
                  },
                }}
                onChange={(event) =>
                  setSelectedApiRoles(event.target.value ? [event.target.value] : [])
                }
              >
                {apiRoleOptions.map((roleOption) => (
                  <MenuItem className={classes.roleSelectMenuItem} key={roleOption.id} value={roleOption.id}>
                    {roleOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        }
        mainButtonText="Create"
        secondaryButtonText="Cancel"
        onMainButtonClick={handleConfirmCreateApiCredentials}
        onSecondaryButtonClick={handleCloseCreateApiCredentialsPopup}
      />
      <Popup
        isOpen={isRegenerateApiCredentialsPopupOpen}
        headlineText="Regenerate API Credentials"
        contentText={`This will generate a new client secret for ${selectedApiCredential?.name || 'this credential'} and revoke the old one. The new secret will be shown only once. Continue?`}
        mainButtonText="Regenerate"
        secondaryButtonText="Cancel"
        onMainButtonClick={handleConfirmRegenerateApiCredentials}
        onSecondaryButtonClick={handleCloseRegenerateApiCredentialsPopup}
      />
      <Popup
        isOpen={isEditApiCredentialsPopupOpen}
        headlineText="Edit API Credential"
        contentText="Update the credential name."
        PopupContent={
          <Box sx={{ width: '100%', maxWidth: '416px', display: 'flex', flexDirection: 'column' }}>
            <Box mt="8px">
              <TextField
                className={classes.copyTextField}
                label="Credential Name *"
                value={editedApiCredentialName}
                fullWidth
                onChange={(event) => setEditedApiCredentialName(event.target.value)}
              />
            </Box>
            <Box mt="12px">
              <TextField
                className={classes.copyTextField}
                label="Select Roles For Generated Token *"
                value={editedApiRoles[0] || ''}
                fullWidth
                select
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      className: classes.roleSelectMenuPaper,
                    },
                  },
                }}
                onChange={(event) =>
                  setEditedApiRoles(event.target.value ? [event.target.value] : [])
                }
              >
                {apiRoleOptions.map((roleOption) => (
                  <MenuItem className={classes.roleSelectMenuItem} key={roleOption.id} value={roleOption.id}>
                    {roleOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        }
        mainButtonText="Save"
        secondaryButtonText="Cancel"
        onMainButtonClick={handleConfirmEditApiCredentials}
        onSecondaryButtonClick={handleCloseEditApiCredentialsPopup}
      />
      <Popup
        isOpen={isDeleteApiCredentialsPopupOpen}
        headlineText="Delete API Credential"
        contentText={`Are you sure you want to delete ${selectedApiCredential?.name || 'this credential'}? This action cannot be undone.`}
        mainButtonText="Delete"
        secondaryButtonText="Cancel"
        onMainButtonClick={handleConfirmDeleteApiCredentials}
        onSecondaryButtonClick={handleCloseDeleteApiCredentialsPopup}
      />
    </Box>
  );
};

export default ManageAccountView;